import {Locale} from '@dms/api/base';
import i18n from '@dms/i18n';

import {isNotNilOrEmpty, Nullish} from 'shared/web';

import {SettingsChecklistItem} from '../types/SettingsChecklistItem';

export interface SettingsChecklistValues {
  customerMessageId: string;
  additionalSettings: Record<string, unknown> | Nullish;
  enabled: boolean | Nullish;
  smsEnabled: boolean | Nullish;
  emailEnabled: boolean | Nullish;
  locales: Locale[];
  smsBodies: Record<string, string | null> | Nullish;
  emailSubjects: Record<string, string | null> | Nullish;
  emailBodies: Record<string, string | null> | Nullish;
  emailCredentialsId: string | Nullish;
}

const CHECK_IN_MESSAGE_ID = 'business_case-check_in_after_sale';
const MIN_SEND_AFTER_DAYS = 1;
const MAX_SEND_AFTER_DAYS = 30;

/**
 * Every tenant locale must carry text: there is no cross-locale fallback, so the send
 * fails outright (DMS-MS-CM-0011) for a customer whose locale has no template.
 */
const isFilledForEveryLocale = (
  record: Record<string, string | null> | Nullish,
  locales: Locale[]
): boolean =>
  isNotNilOrEmpty(locales) && locales.every((locale) => isNotNilOrEmpty(record?.[locale]?.trim()));

// Record-specific conditions (vehicle dates, order states, contact data) are left out:
// they cannot be satisfied on this screen.
export const getSettingsChecklistItems = (
  values: SettingsChecklistValues
): SettingsChecklistItem[] => {
  const isSmsEnabled = values.smsEnabled === true;
  const isEmailEnabled = values.emailEnabled === true;
  const sendAfterDays = values.additionalSettings?.sendAfterDays;
  const sendAtTime = values.additionalSettings?.sendAtTime;

  return [
    {
      id: 'enabled',
      title: i18n.t('page.settings.scheduledMessages.checklist.enabled'),
      isMet: values.enabled === true,
    },
    {
      id: 'channel',
      title: i18n.t('page.settings.scheduledMessages.checklist.channel'),
      isMet: isSmsEnabled || isEmailEnabled,
    },
    ...(values.customerMessageId === CHECK_IN_MESSAGE_ID
      ? [
          {
            id: 'whenToSend',
            title: i18n.t('page.settings.scheduledMessages.checklist.whenToSend'),
            isMet:
              typeof sendAfterDays === 'number' &&
              Number.isInteger(sendAfterDays) &&
              sendAfterDays >= MIN_SEND_AFTER_DAYS &&
              sendAfterDays <= MAX_SEND_AFTER_DAYS &&
              typeof sendAtTime === 'string' &&
              isNotNilOrEmpty(sendAtTime),
          },
        ]
      : []),
    ...(isSmsEnabled
      ? [
          {
            id: 'smsBody',
            title: i18n.t('page.settings.scheduledMessages.checklist.smsBody'),
            isMet: isFilledForEveryLocale(values.smsBodies, values.locales),
          },
        ]
      : []),
    ...(isEmailEnabled
      ? [
          {
            id: 'emailSubject',
            title: i18n.t('page.settings.scheduledMessages.checklist.emailSubject'),
            isMet: isFilledForEveryLocale(values.emailSubjects, values.locales),
          },
          {
            id: 'emailBody',
            title: i18n.t('page.settings.scheduledMessages.checklist.emailBody'),
            isMet: isFilledForEveryLocale(values.emailBodies, values.locales),
          },
          {
            id: 'smtpCredentials',
            title: i18n.t('page.settings.scheduledMessages.checklist.smtpCredentials'),
            isMet: isNotNilOrEmpty(values.emailCredentialsId),
          },
        ]
      : []),
  ];
};
